import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import AccountInfo from '@/components/AccountInfo'
import AuthWindow from '@/components/AuthWindow'
import Subscription from '@/components/Subscription'
import ImportChatGPT from '@/components/ImportChatGPT'
import Button from '@/components/ui/Button'
import { useThemeMode } from '@/contexts/ThemeContext'
import useIsMobile from '@/hooks/useIsMobile'
import useIsCondensed from '@/hooks/useIsCondensed'
import { signOut } from '@/lib/auth'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { clearGuestSession } from '@/lib/guestStorage'
import { theme } from '@/lib/theme'

interface TopNavProps {
  fontMode: 'departure-mono' | 'open-dyslexic' | 'system'
  onFontChange: (font: 'departure-mono' | 'open-dyslexic' | 'system') => void
  onThemeToggle: () => void
  windows: Array<{
    id: number
    title: string
    isActive: boolean
    contentType?: string
    isMinimized?: boolean
  }>
  onWindowClick: (id: number) => void
  onWindowClose?: (id: number) => void
  activeWindowId: number | null
  onOpenChat?: () => void
  onOpenSettings?: () => void
  onOpenProfile?: () => void
  onOpenFileExplorer?: () => void
  onOpenRepresentation?: () => void
  isGuestMode?: boolean
  onLoginClick?: () => void
  showLoginModal?: boolean
  onLoginSuccess?: () => void
  onCloseLoginModal?: () => void
}

export default function TopNav({
  fontMode,
  onFontChange,
  onThemeToggle,
  windows,
  onWindowClick,
  onWindowClose,
  activeWindowId,
  onOpenChat,
  onOpenSettings,
  onOpenProfile,
  onOpenFileExplorer,
  onOpenRepresentation,
  isGuestMode = false,
  onLoginClick,
  showLoginModal = false,
  onLoginSuccess,
  onCloseLoginModal,
}: TopNavProps) {
  const { isDark, colors } = useThemeMode()
  const { t, i18n } = useTranslation()

  // NOTE: useIsMobile detects touch devices (coarse pointer / mobile UA), not viewport width.
  // For width-based responsive UI, use useIsCondensed.
  const isMobile = useIsMobile()
  const isCondensed = useIsCondensed(1000) // Tabs become icons below 1000px (width-based)
  const isNarrow = useIsCondensed(850) // Hide social icons below 850px (width-based)
  const isCompactTabs = isMobile || isCondensed // Compact on touch devices OR narrow viewports
  const loginPopdownRef = useRef<HTMLDivElement>(null)
  const menuDropdownRef = useRef<HTMLDivElement>(null)
  const [showMenuDropdown, setShowMenuDropdown] = useState(false)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showImportChatGPTModal, setShowImportChatGPTModal] = useState(false)
  const [showAccountModal, setShowAccountModal] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  // Close popdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!showLoginModal) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        loginPopdownRef.current &&
        !loginPopdownRef.current.contains(event.target as Node)
      ) {
        onCloseLoginModal?.()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCloseLoginModal?.()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [showLoginModal, onCloseLoginModal])

  // Close menu dropdown when clicking outside or pressing Escape
  useEffect(() => {
    if (!showMenuDropdown) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuDropdownRef.current &&
        !menuDropdownRef.current.contains(event.target as Node)
      ) {
        setShowMenuDropdown(false)
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMenuDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscapeKey)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [showMenuDropdown])

  const handleLogout = async () => {
    setIsLoggingOut(true)
    try {
      const result = await signOut()
      if (result.error) {
        console.error('Logout error:', result.error)
        setIsLoggingOut(false)
        return
      }

      // Clear guest session if any
      clearGuestSession()

      // Reload the page to reset the app state
      window.location.reload()
    } catch (error) {
      console.error('Logout error:', error)
      setIsLoggingOut(false)
    }
  }

  const navItems = [
    {
      title: t('navigation.chat'),
      icon: normalizeEmojis('💬'),
      action: onOpenChat,
      type: 'chat',
    },
    {
      title: t('navigation.fileExplorer'),
      icon: normalizeEmojis('📁'),
      action: onOpenFileExplorer,
      type: 'fileExplorer',
    },
    {
      title: t('navigation.settings'),
      icon: normalizeEmojis('⚙️'),
      action: onOpenSettings,
      type: 'settings',
    },
    {
      title: t('navigation.profile'),
      icon: normalizeEmojis('👤'),
      action: onOpenProfile,
      type: 'profile',
    },
    {
      title: t('navigation.representation'),
      icon: normalizeEmojis('🧠'),
      action: onOpenRepresentation,
      type: 'representation',
    },
  ]

  return (
    <div
      suppressHydrationWarning
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '40px',
        backgroundColor: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        display: 'flex',
        alignItems: 'center',
        padding: isCompactTabs ? '0 8px' : '0 12px',
        zIndex: 1000,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        fontFamily:
          fontMode === 'departure-mono'
            ? 'var(--font-departure-mono)'
            : fontMode === 'open-dyslexic'
              ? 'var(--font-open-dyslexic)'
              : 'inherit',
      }}
    >
      {/* Logo/Home */}
      <a
        href="https://honcho.chat"
        suppressHydrationWarning
        style={{
          marginRight: isCompactTabs ? '8px' : '24px',
          fontSize: isCompactTabs ? '12px' : '14px',
          fontWeight: 'bold',
          color: colors.visualAccent1,
          letterSpacing: '1px',
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: isCompactTabs ? '6px' : '10px',
          flexShrink: 0,
        }}
      >
        <svg width={isCompactTabs ? '18' : '20'} height={isCompactTabs ? '16' : '18'} viewBox="0 0 87 77" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginTop: '-2px' }}>
          <path d="M55.29 0H38.77V6.89H55.29V0Z" fill="currentColor"/>
          <path d="M65.95 6.89H55.29V13.69H65.95V6.89Z" fill="currentColor"/>
          <path d="M72.66 13.69H65.95V20.4H72.66V13.69Z" fill="currentColor"/>
          <path d="M79.54 20.4H72.66V30.17H79.54V20.4Z" fill="currentColor"/>
          <path d="M86.33 30.16H79.54V46.54H86.33V30.16Z" fill="currentColor"/>
          <path d="M79.54 46.55H72.67V56.32H79.54V46.55Z" fill="currentColor"/>
          <path d="M72.67 56.31H65.95V63.11H72.67V56.31Z" fill="currentColor"/>
          <path d="M65.95 63.11H55.29V69.96H65.95V63.11Z" fill="currentColor"/>
          <path d="M55.29 69.96H35.84V76.65H55.29V69.96Z" fill="currentColor"/>
          <path d="M35.84 63.11H25.18V69.96H35.84V63.11Z" fill="currentColor"/>
          <path d="M25.18 56.31H18.39V63.11H25.18V56.31Z" fill="currentColor"/>
          <path d="M18.39 46.55H11.52V56.32H18.39V46.55Z" fill="currentColor"/>
          <path d="M4.87 33.97V20.4H0V40.8H4.87V46.55H11.52V33.97H4.87Z" fill="currentColor"/>
          <path d="M18.31 27.17H11.52V33.96H18.31V27.17Z" fill="currentColor"/>
          <path d="M38.77 6.89H18.39V13.69H38.77V6.89Z" fill="currentColor"/>
          <path d="M45.5 13.69H38.79V20.4H45.5V13.69Z" fill="currentColor"/>
          <path d="M18.32 27.17H38.79V20.4H18.39V13.69H4.87V20.4H18.32V27.17Z" fill="currentColor"/>
          <path d="M38.79 30.16H31.98V36.87H38.79V30.16Z" fill="currentColor"/>
          <path d="M59.09 30.16H52.28V36.87H59.09V30.16Z" fill="currentColor"/>
          <path d="M59.16 50.47H31.98V56.31H59.16V50.47Z" fill="currentColor"/>
        </svg>
        {!isMobile && 'Honcho Chat'}
      </a>

      {/* Navigation Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '2px', 
        flex: 1,
        overflowX: isCompactTabs ? 'auto' : 'visible',
        overflowY: 'hidden',
        // Hide scrollbar but keep functionality
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      } as React.CSSProperties}>
        {navItems.map((item) => {
          const window = windows.find((w) => w.contentType === item.type)
          const windowExists = !!window
          const isActive = window?.isActive
          const isMinimized = window?.isMinimized

          return (
            <button
              key={item.type}
              suppressHydrationWarning
              data-nav-type={item.type}
              onClick={() => {
                const existingWindow = windows.find(
                  (w) => w.contentType === item.type
                )
                if (existingWindow) {
                  onWindowClick(existingWindow.id)
                } else if (item.action) {
                  item.action()
                }
              }}
              style={{
                height: '40px',
                padding: isCompactTabs ? '8px 12px' : '6px 16px',
                background: isActive ? colors.navTabBgActive : 'transparent',
                border: 'none',
                borderRadius: '6px 6px 0 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: isCompactTabs ? '4px' : '6px',
                color: isActive ? colors.primaryText : colors.secondaryText,
                fontSize: isCompactTabs ? '11px' : '12px',
                fontWeight: isActive ? '600' : '500',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                transition: 'all 0.15s ease',
                borderTop: isActive
                  ? `2px solid ${colors.primaryText}`
                  : '2px solid transparent',
                marginTop: isActive ? '0' : '2px',
                opacity: !windowExists ? 0.5 : isMinimized ? 0.6 : 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = colors.navTabBgHover
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }
              }}
            >
              <span style={{ fontSize: isCompactTabs ? '18px' : '16px' }}>{item.icon}</span>
              {!isCompactTabs && (
                <span>
                  {i18n.language === 'en' && item.title.length > 0 ? (
                    <>
                      <span style={{ textDecoration: 'underline' }}>
                        {item.title[0]}
                      </span>
                      {item.title.slice(1)}
                    </>
                  ) : (
                    item.title
                  )}
                </span>
              )}
            </button>
          )
        })}

        {/* Show open windows */}
        {windows
          .filter((w) => !navItems.some((n) => n.type === w.contentType))
          .map((win) => {
            return (
              <button
                key={win.id}
                suppressHydrationWarning
                onClick={() => onWindowClick(win.id)}
                style={{
                  padding: '6px 16px',
                  background:
                    win.id === activeWindowId
                      ? colors.navTabBgActive
                      : 'transparent',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  color:
                    win.id === activeWindowId
                      ? colors.primaryText
                      : colors.secondaryText,
                  fontSize: '12px',
                  fontWeight: win.id === activeWindowId ? '600' : '500',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  transition: 'all 0.15s ease',
                  borderTop:
                    win.id === activeWindowId
                      ? `2px solid ${colors.primaryText}`
                      : '2px solid transparent',
                  marginTop: win.id === activeWindowId ? '0' : '2px',
                  opacity: win.isMinimized ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (win.id !== activeWindowId) {
                    e.currentTarget.style.backgroundColor = colors.navTabBgHover
                  }
                }}
                onMouseLeave={(e) => {
                  if (win.id !== activeWindowId) {
                    e.currentTarget.style.backgroundColor = 'transparent'
                  }
                }}
              >
                <span>{win.title}</span>
              </button>
            )
          })}
      </div>

      {/* Right side controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isCompactTabs ? '6px' : '12px', flexShrink: 0 }}>
        {/* Close button for active window on mobile */}
        {isMobile && activeWindowId !== null && windows.length > 0 && onWindowClose && (
          <button
            onClick={() => onWindowClose(activeWindowId)}
            style={{
              padding: '6px 10px',
              backgroundColor: 'transparent',
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: colors.primaryText,
              fontSize: '16px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.secondaryBg
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
          >
            ✕
          </button>
        )}
        
        {/* Social Links - hide on mobile and narrow widths */}
        {!isMobile && !isNarrow && (
          <div style={{ display: 'flex', gap: '12px', marginRight: '14px', alignItems: 'center' }}>
            <a
              href="https://twitter.com/honchodotdev"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.secondaryText,
                opacity: 0.7,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
              title="Twitter"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.84 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
              </svg>
            </a>
            <a
              href="https://github.com/plastic-labs/honcho"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.secondaryText,
                opacity: 0.7,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
              title="GitHub"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
            </a>
            <a
              href="https://discord.gg/honcho"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: colors.secondaryText,
                opacity: 0.7,
                transition: 'opacity 0.2s',
                display: 'flex',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.7')}
              title="Discord"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
            </a>
          </div>
        )}

        {/* Login button for guest users */}
        {isGuestMode && onLoginClick && (
          <button
            onClick={onLoginClick}
            data-login-button
            style={{
              padding: isCompactTabs ? '4px 8px' : '4px 12px',
              backgroundColor: colors.visualAccent1,
              color: '#ffffff',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: isCompactTabs ? '11px' : '12px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.85'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1'
            }}
          >
            {isCompactTabs ? t('chat.header.loginButton').slice(0, 5) : t('chat.header.loginButton')}
          </button>
        )}

        {/* Theme Toggle */}
        <button
          onClick={onThemeToggle}
          suppressHydrationWarning
          style={{
            marginRight: isCompactTabs ? '4px' : '14px',
            padding: '4px 8px',
            background: 'transparent',
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: colors.primaryText,
            fontSize: isCompactTabs ? '13px' : '14px',
            transition: 'all 0.15s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.secondaryBg
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent'
          }}
        >
          {isDark ? '🌙' : '☀︎'}
        </button>

        {/* Language Selector - hide on mobile and compact mode */}
        {!isMobile && !isCompactTabs && (
          <select
            value={i18n.language}
            suppressHydrationWarning
            onChange={(e) => i18n.changeLanguage(e.target.value)}
            style={{
              marginRight: '14px',
              padding: '4px 8px',
              backgroundColor: colors.secondaryBg,
              color: colors.primaryText,
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '4px',
              fontSize: '11px',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="en">🇺🇸 EN</option>
            <option value="ja">🇯🇵 JA</option>
          </select>
        )}

        {/* Three-line Menu Button - Only show for logged-in users */}
        {!isGuestMode && (
        <div style={{ position: 'relative', marginRight: '8px' }} ref={menuDropdownRef}>
          <button
            suppressHydrationWarning
            onClick={() => setShowMenuDropdown(!showMenuDropdown)}
            style={{
              padding: '4px 8px',
              background: showMenuDropdown ? colors.secondaryBg : 'transparent',
              border: `1px solid ${colors.primaryBorder}`,
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column',
              gap: '3px',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
              width: '28px',
              height: '28px',
            }}
            onMouseEnter={(e) => {
              if (!showMenuDropdown) {
                e.currentTarget.style.backgroundColor = colors.secondaryBg
              }
            }}
            onMouseLeave={(e) => {
              if (!showMenuDropdown) {
                e.currentTarget.style.backgroundColor = 'transparent'
              }
            }}
          >
            <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
            <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
            <div style={{ width: '14px', height: '2px', backgroundColor: colors.primaryText, borderRadius: '1px' }} />
          </button>

          {/* Dropdown Menu */}
          {showMenuDropdown && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                backgroundColor: colors.primaryBg,
                border: `1px solid ${colors.primaryBorder}`,
                borderRadius: '6px',
                boxShadow: isDark
                  ? '0 8px 24px rgba(0, 0, 0, 0.6), 0 4px 8px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)'
                  : '0 8px 24px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(0, 0, 0, 0.2)',
                minWidth: '160px',
                overflow: 'hidden',
                zIndex: 1001,
              }}
            >
              <button
                onClick={() => {
                  setShowMenuDropdown(false)
                  ;(window as any).desktopTutorial?.restart?.()
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: colors.primaryText,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryBg
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>❓</span> {t('menu.restartTutorial')}
              </button>
              <button
                onClick={() => {
                  setShowMenuDropdown(false)
                  setShowAccountModal(true)
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: colors.primaryText,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryBg
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>👤</span> {t('menu.account')}
              </button>
              <button
                onClick={() => {
                  setShowMenuDropdown(false)
                  setShowSubscriptionModal(true)
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: colors.primaryText,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryBg
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>💳</span> {t('menu.subscription')}
              </button>
              <button
                onClick={() => {
                  setShowMenuDropdown(false)
                  setShowImportChatGPTModal(true)
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: colors.primaryText,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryBg
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>📥</span> {t('menu.importFromChatGPT')}
              </button>
              <div style={{ height: '1px', backgroundColor: colors.primaryBorder, margin: '4px 0' }} />
              <button
                onClick={() => {
                  setShowMenuDropdown(false)
                  setShowLogoutConfirm(true)
                }}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondaryBg
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <span>🚪</span> {t('menu.logOut')}
              </button>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Login Popdown */}
      {onLoginSuccess && onCloseLoginModal && (
        <div
          ref={loginPopdownRef}
          className="login-popdown"
          style={{
            display: showLoginModal ? 'block' : 'none',
          }}
        >
          <AuthWindow
            onAuthSuccess={onLoginSuccess}
            extraData={{ mode: 'login' }}
          />
        </div>
      )}

      {/* Account Info Modal */}
      {showAccountModal && (
        <AccountInfo onClose={() => setShowAccountModal(false)} />
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <Subscription onClose={() => setShowSubscriptionModal(false)} />
      )}

      {/* Import from ChatGPT Modal */}
      {showImportChatGPTModal && (
        <ImportChatGPT 
          onClose={() => setShowImportChatGPTModal(false)}
          onOpenSubscription={() => {
            setShowImportChatGPTModal(false)
            setShowSubscriptionModal(true)
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm &&
        createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
              padding: theme.spacing.xl,
              fontFamily:
                fontMode === 'departure-mono'
                  ? 'var(--font-departure-mono), monospace'
                  : fontMode === 'open-dyslexic'
                    ? 'var(--font-open-dyslexic), sans-serif'
                    : '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
            onClick={() => setShowLogoutConfirm(false)}
          >
            <div
              style={{
                backgroundColor: colors.primaryBg,
                borderRadius: '8px',
                border: `1px solid ${colors.primaryBorder}`,
                padding: theme.spacing.xl,
                maxWidth: '400px',
                width: '90%',
                position: 'relative',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                style={{
                  fontSize: theme.fontSize.lg,
                  fontWeight: '600',
                  marginBottom: theme.spacing.md,
                  color: colors.primaryText,
                }}
              >
                Confirm Logout
              </h3>
              <p
                style={{
                  fontSize: theme.fontSize.md,
                  color: colors.secondaryText,
                  marginBottom: theme.spacing.lg,
                }}
              >
                Are you sure you want to log out?
              </p>
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.sm,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  onClick={() => setShowLogoutConfirm(false)}
                  variant="secondary"
                  size="sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setShowLogoutConfirm(false)
                    handleLogout()
                  }}
                  variant="destructive"
                  size="sm"
                  disabled={isLoggingOut}
                  isLoading={isLoggingOut}
                >
                  Log Out
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}
